export type MainCardShellContent = Record<string, unknown>;

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/** Turn "Single Card" into "single_card". */
export const normalizeMainCardTypeKey = (type?: string | null): string =>
  String(type ?? "")
    .replace(/[\s-]+/g, "_")
    .replace(/__+/g, "_")
    .toLowerCase();

/** Read cover fields from `card.content`. */
export const getMainCardContent = (card?: Record<string, unknown> | null): MainCardShellContent => {
  if (!card || !isObject(card.content)) return {};
  return card.content;
};

/** Read card kind from `card.type`. */
export const getMainCardType = (card?: Record<string, unknown> | null): string => {
  if (!card || typeof card.type !== "string") return "";
  return card.type.trim();
};

/** Product id: `ref_id`, then `content.id`. */
export const getMainCardRefId = (card?: Record<string, unknown> | null): number | undefined => {
  if (!card) return undefined;

  const refId = Number(card.ref_id);
  if (Number.isFinite(refId) && refId > 0) return refId;

  const contentId = Number(getMainCardContent(card).id);
  if (Number.isFinite(contentId) && contentId > 0) return contentId;

  return undefined;
};

/** Bought when `purchase` is not null. */
export const isMainCardPurchased = (card?: Record<string, unknown> | null): boolean =>
  Boolean(card && card.purchase != null);

/** True for Single_Card, Demo_Card, Season_Card, or Impact_Card. */
export const isMainCardShellType = (type?: string | null): boolean => {
  const key = normalizeMainCardTypeKey(type);
  return (
    key === "single_card" ||
    key === "demo_card" ||
    key === "season_card" ||
    key === "impact_card" ||
    key === "impact"
  );
};

/** Build the cover as book page 0 from `content`. */
const makeCoverPage = (
  content: MainCardShellContent,
  type: string,
  refId: number | undefined
): Record<string, unknown> => ({
  id: Number(content.id ?? refId ?? 0) || 0,
  order: 0,
  type: type || "",
  ref_id: refId,
  content,
  assets: [] as number[],
  __synthetic_cover: true,
});

/**
 * Book pages for the UI:
 * - page 0 = cover from `content`
 * - then the real `pages` from the API (Audio, Text, …)
 */
export const resolveMainCardShellPages = (
  card: Record<string, unknown>,
  options?: { isPurchased?: boolean }
): Record<string, unknown>[] => {
  const apiPages = Array.isArray(card.pages) ? (card.pages as Record<string, unknown>[]) : [];
  const type = getMainCardType(card);
  const refId = getMainCardRefId(card);
  const purchased = options?.isPurchased ?? isMainCardPurchased(card);
  const rawContent = getMainCardContent(card);
  const content = { ...rawContent, is_purchased: purchased };

  const hasCover = Boolean(type) || Object.keys(rawContent).length > 0;
  if (!hasCover) return apiPages;

  return [makeCoverPage(content, type, refId), ...apiPages];
};

/** Cover content: prefer `content`, else book page 0 (after resolve). */
export const getPublishedCardShellContent = (
  card?: { pages?: Array<{ content?: Record<string, unknown> }>; content?: unknown } | null
): Record<string, unknown> => {
  if (!card) return {};
  if (isObject(card.content)) return card.content;
  return (card.pages?.[0]?.content ?? {}) as Record<string, unknown>;
};

/** Bought when `purchase` is set, or cover page says is_purchased. */
export const isPublishedCardPurchased = (
  card?: {
    purchase?: unknown;
    content?: { is_purchased?: boolean };
    pages?: Array<{ content?: { is_purchased?: boolean } }>;
  } | null
): boolean => {
  if (!card) return false;
  if (card.purchase != null) return true;
  if (isObject(card.content) && card.content.is_purchased != null) {
    return Boolean(card.content.is_purchased);
  }
  if (card.pages?.[0]?.content?.is_purchased != null) {
    return Boolean(card.pages[0].content.is_purchased);
  }
  return false;
};
